import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import invoiceService from '../../services/invoice';
import './styles.css';
import firebase from '@config/firebase.config';
const database = firebase.firestore();
const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });

  const authState = useSelector((state) => ({ ...state.auth }));
  const companyId = authState.profile?.activeCompany?.companyId;

  // Fetch invoices from API
  const fetchInvoices = async (page = 1) => {
    if (!companyId) {
      setError('Company ID not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const invoicesRef = database
        .collection('companyDatabase')
        .doc(companyId)
        .collection('invoices');
      const invoicesSnapshot = await invoicesRef.get();
      const invoices = invoicesSnapshot.docs.map((doc) => doc.data());
      if (invoices.length > 0) {
        invoices.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
        setInvoices(invoices);
        setPagination({
          page: page,
          limit: pagination.limit,
          total: invoices.length,
          totalPages: Math.ceil(invoices.length / pagination.limit),
        });
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('An error occurred while fetching invoices');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchInvoices(1);
  }, [companyId]);

  // Handle row click to open Stripe invoice
  const handleInvoiceClick = (invoice) => {
    if (invoice.shortUrl) {
      window.open(invoice.shortUrl, '_blank');
    }
  };

  // Removed client-side search filtering

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchInvoices(newPage);
    }
  };

  // Generate pagination items
  const generatePaginationItems = () => {
    const items = [];
    const currentPage = pagination.page;
    const totalPages = pagination.totalPages;

    // Previous button
    items.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink
          previous
          onClick={() => handlePageChange(currentPage - 1)}
        />
      </PaginationItem>
    );

    // Page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    items.push(
      <PaginationItem key="next" disabled={currentPage === totalPages}>
        <PaginationLink
          next
          onClick={() => handlePageChange(currentPage + 1)}
        />
      </PaginationItem>
    );

    return items;
  };
  const getInvoiceType = (type) => {
    switch (type) {
      case 'subscription':
        return 'Subscription Payment';
      case 'refund':
        return 'Refund Payment';
      case 'upgrade':
        return 'New Seat Payment';
      case 'failed':
        return 'Failed Payment';
      default:
        return '-';
    }
  };
  return (
    <Container className="pt-md-2 invoices-page" fluid>
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Invoices</h3>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {error && (
                <Alert color="danger">
                  <strong>Error:</strong> {error}
                  <Button
                    color="danger"
                    size="sm"
                    className="ml-2"
                    onClick={() => fetchInvoices(pagination.page)}
                  >
                    Retry
                  </Button>
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading invoices...</p>
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-4">
                  <i
                    className="ni ni-archive-2 text-muted"
                    style={{ fontSize: '4rem' }}
                  ></i>
                  <h4 className="text-muted mt-3">No invoices found</h4>
                  <p className="text-muted">
                    Invoices will appear here once you have active
                    subscriptions.
                  </p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="align-items-center table-flush">
                      <thead className="thead-light">
                        <tr>
                          <th scope="col">Invoice #</th>
                          <th scope="col">Type</th>
                          <th scope="col">Date</th>
                          <th scope="col">Amount</th>
                          <th scope="col">Plan</th>
                          <th scope="col">Status</th>
                          <th scope="col">Billing Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr
                            key={invoice.id}
                            className="invoice-row"
                            onClick={() => handleInvoiceClick(invoice)}
                          >
                            <td>
                              <span className="mb-0 text-sm font-weight-bold">
                                {invoice.invoiceNumber}
                              </span>
                            </td>
                            <td>
                              <span className="mb-0 text-sm font-weight-bold">
                                {getInvoiceType(invoice.type)}
                              </span>
                            </td>
                            <td>
                              <span className="mb-0 text-sm">
                                {invoiceService.formatDate(
                                  invoice.payment?.paidAt?.seconds * 1000
                                )}
                              </span>
                            </td>
                            <td>
                              <span className="mb-0 text-sm font-weight-bold">
                                {invoiceService.formatCurrency(
                                  invoice.payment?.amount,
                                  invoice.currency
                                )}
                              </span>
                            </td>
                            <td>
                              <span className="mb-0 text-sm">
                                {invoice.planName}
                              </span>
                            </td>
                            <td>
                              <Badge
                                color={invoiceService.getStatusBadgeColor(
                                  invoice.status
                                )}
                                pill
                              >
                                {invoiceService.getStatusText(invoice.status)}
                              </Badge>
                            </td>
                            <td>
                              <span className="mb-0 text-sm">
                                {invoiceService.formatBillingPeriod(
                                  invoice.billingPeriod
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <Row className="mt-3">
                      <Col className="d-flex justify-content-between align-items-center">
                        <div className="text-muted">
                          Showing {(pagination.page - 1) * pagination.limit + 1}{' '}
                          to{' '}
                          {Math.min(
                            pagination.page * pagination.limit,
                            pagination.total
                          )}{' '}
                          of {pagination.total} invoices
                        </div>
                        <Pagination className="pagination justify-content-end mb-0">
                          {generatePaginationItems()}
                        </Pagination>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default InvoicesPage;
